import React, { useMemo, useRef, useState, useEffect } from 'react';
import './TagInput.css';
import { normalizeTagValue } from '../utils/tagUtils';

const noop = () => {};

const toArray = (value) => (Array.isArray(value) ? value : (value ? [value] : []));

const TagInput = ({
    name = 'tags',
    selectedTags = [],
    availableTags = [],
    onChange = noop,
    placeholder = 'Add a tag',
    disabled = false,
    commitOnBlur = false,
    required = false,
    maxSuggestions = 6,
}) => {
    const tagsArray = toArray(selectedTags);
    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const blurTimeoutRef = useRef(null);
    const inputRef = useRef(null);

    const normalizedSelected = useMemo(() => {
        const set = new Set();
        tagsArray.forEach((tag) => {
            const normalized = normalizeTagValue(tag);
            if (normalized) {
                set.add(normalized);
            }
        });
        return set;
    }, [tagsArray]);

    const availableTagMap = useMemo(() => {
        const map = new Map();
        availableTags.forEach((tag) => {
            const normalized = normalizeTagValue(tag);
            if (!normalized || map.has(normalized)) {
                return;
            }
            map.set(normalized, tag);
        });
        return map;
    }, [availableTags]);

    const suggestions = useMemo(() => {
        const term = normalizeTagValue(inputValue);
        if (!term) {
            return [];
        }

        const matches = [];
        availableTagMap.forEach((original, normalized) => {
            if (!normalizedSelected.has(normalized) && normalized.includes(term)) {
                matches.push(original);
            }
        });

        return matches.slice(0, maxSuggestions);
    }, [inputValue, availableTagMap, normalizedSelected, maxSuggestions]);

    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const emitTags = (nextTags) => {
        if (disabled) {
            return;
        }
        onChange(nextTags);
    };

    const handleAddTag = (rawTag) => {
        const normalized = normalizeTagValue(rawTag);
        if (!normalized || normalizedSelected.has(normalized)) {
            setInputValue('');
            return;
        }

        const canonicalTag = availableTagMap.get(normalized) || normalized;
        emitTags([...tagsArray, canonicalTag]);
        setInputValue('');
    };

    const handleRemoveTagAtIndex = (indexToRemove) => {
        emitTags(tagsArray.filter((_, index) => index !== indexToRemove));
    };

    const handleInputKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === 'Enter' || event.key === ',' || event.key === 'Tab') {
            const pendingValue = inputValue.trim();
            if (pendingValue.length > 0) {
                event.preventDefault();
                handleAddTag(pendingValue);
            }
            return;
        }

        if (event.key === 'Backspace' && inputValue.length === 0 && tagsArray.length > 0) {
            event.preventDefault();
            handleRemoveTagAtIndex(tagsArray.length - 1);
        }
    };

    const handleSuggestionMouseDown = (event) => {
        event.preventDefault();
    };

    const handleSuggestionClick = (suggestion) => {
        handleAddTag(suggestion);
    };

    const handleBlur = () => {
        const pendingValue = inputValue.trim();
        if (commitOnBlur && pendingValue.length > 0) {
            handleAddTag(pendingValue);
        }

        blurTimeoutRef.current = setTimeout(() => {
            setIsFocused(false);
            setInputValue('');
        }, 120);
    };

    const handleFocus = () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }
        setIsFocused(true);
    };

    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    return (
        <div className="tag-input">
            <div
                className={`tag-input__control${isFocused ? ' tag-input__control--focused' : ''}${disabled ? ' tag-input__control--disabled' : ''}`}
                onClick={handleContainerClick}
            >
                <div className="tag-input__chips" aria-label="Selected tags">
                    {tagsArray.map((tag, index) => (
                        <button
                            key={`${tag}-${index}`}
                            type="button"
                            className="tag-input__chip"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleRemoveTagAtIndex(index);
                            }}
                            disabled={disabled}
                        >
                            <span>{tag}</span>
                            <span aria-hidden="true" className="tag-input__chip-remove">×</span>
                            <span className="sr-only">Remove {tag}</span>
                        </button>
                    ))}
                </div>

                <input
                    ref={inputRef}
                    className="tag-input__input"
                    type="text"
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    onKeyDown={handleInputKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    placeholder={tagsArray.length === 0 ? placeholder : ''}
                    disabled={disabled}
                    aria-label={placeholder}
                />
            </div>

            {isFocused && suggestions.length > 0 && (
                <div className="tag-input__suggestions">
                    {suggestions.map((suggestion) => (
                        <button
                            key={suggestion}
                            type="button"
                            className="tag-input__suggestion"
                            onMouseDown={handleSuggestionMouseDown}
                            onClick={() => handleSuggestionClick(suggestion)}
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            )}

            <input
                type="hidden"
                name={name}
                value={tagsArray.join(',')}
                readOnly
            />
        </div>
    );
};

export default TagInput;
